// frontend/src/services/nbaApi.js
// BallDontLie NBA API integration layer
// Docs: https://docs.balldontlie.io / https://nba.balldontlie.io

const BASE_URL = 'https://api.balldontlie.io/nba/v1';

const getHeaders = () => ({
  Authorization: `Bearer ${process.env.REACT_APP_BDL_API_KEY || ''}`,
});

/**
 * Generic fetch wrapper with error handling
 */
async function apiFetch(endpoint, params = {}) {
  const url = new URL(`${BASE_URL}${endpoint}`);

  // Handle array params (e.g. player_ids[], seasons[])
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    if (Array.isArray(value)) {
      value.forEach((v) => url.searchParams.append(`${key}[]`, v));
    } else {
      url.searchParams.set(key, value);
    }
  });

  const res = await fetch(url.toString(), { headers: getHeaders() });

  if (!res.ok) {
    const body = await res.text().catch(() => '');
    const err = new Error(
      res.status === 401
        ? 'API key is invalid or missing. Please check your BallDontLie API key.'
        : res.status === 429
        ? 'Rate limit reached — please wait a moment and try again.'
        : res.status >= 500
        ? 'BallDontLie servers are temporarily unavailable. Please try again shortly.'
        : `BallDontLie API error (${res.status}): ${body}`
    );
    err.status = res.status;
    throw err;
  }

  return res.json();
}

// ─── Players ────────────────────────────────────────────────────────────────

/**
 * Search players by name.
 * Returns { data: [{ id, first_name, last_name, position, team {...} }] }
 */
export async function searchPlayers(query) {
  if (!query || query.trim().length < 2) return { data: [] };
  return apiFetch('/players', { search: query.trim(), per_page: 15 });
}

/**
 * Get a single player by ID.
 */
export async function getPlayer(playerId) {
  return apiFetch(`/players/${playerId}`);
}

// ─── Stats (Game Logs) ─────────────────────────────────────────────────────

/**
 * Fetch game-by-game stats for one or more players.
 *
 * @param {Object} options
 * @param {number[]} options.player_ids  – required
 * @param {number[]} options.seasons     – e.g. [2025]
 * @param {string}   options.start_date  – YYYY-MM-DD
 * @param {string}   options.end_date    – YYYY-MM-DD
 * @param {boolean}  options.postseason
 * @param {number}   options.per_page    – max 100
 * @param {string}   options.cursor      – pagination cursor
 */
export async function getStats(options = {}) {
  return apiFetch('/stats', options);
}

/**
 * Convenience: fetch the last N games for a player in the current season.
 * Fetches up to 100 game logs and returns the most recent `count`.
 */
export async function getRecentGameLogs(playerId, season, count = 20) {
  // Fetch a generous batch (up to 100) and slice to `count`
  const res = await apiFetch('/stats', {
    player_ids: [playerId],
    seasons: [season],
    per_page: 100,
  });

  // API returns oldest-first; reverse so most recent is first
  const sorted = (res.data || []).sort((a, b) => {
    const dateA = new Date(a.game?.date || 0);
    const dateB = new Date(b.game?.date || 0);
    return dateB - dateA;
  });

  return sorted.slice(0, count);
}

// ─── Season Averages ────────────────────────────────────────────────────────

/**
 * Get season averages for a player.
 *
 * @param {number} playerId
 * @param {number} season     – e.g. 2025 for 2025-26 season
 * @param {string} seasonType – 'regular' | 'playoffs'
 */
export async function getSeasonAverages(playerId, season, seasonType = 'regular') {
  return apiFetch('/season_averages/general', {
    player_id: playerId,
    season,
    season_type: seasonType,
    type: 'base',
  });
}

// ─── Helpers ────────────────────────────────────────────────────────────────

/**
 * Compute rolling averages from an array of game-log stat objects.
 * @param {Object[]} gameLogs – sorted most-recent-first
 * @param {number}   window   – number of games to average over (5, 10, 20, …)
 * @returns {Object|null}      – averaged stat line or null if not enough games
 */
export function computeRollingAverage(gameLogs, window) {
  const slice = gameLogs.slice(0, window);
  if (slice.length === 0) return null;

  const n = slice.length;
  const sum = (key) => slice.reduce((s, g) => s + (Number(g[key]) || 0), 0);

  return {
    games: n,
    pts: (sum('pts') / n).toFixed(1),
    reb: (sum('reb') / n).toFixed(1),
    ast: (sum('ast') / n).toFixed(1),
    stl: (sum('stl') / n).toFixed(1),
    blk: (sum('blk') / n).toFixed(1),
    fgm: (sum('fgm') / n).toFixed(1),
    fga: (sum('fga') / n).toFixed(1),
    fg_pct: n > 0 ? ((sum('fgm') / (sum('fga') || 1)) * 100).toFixed(1) : '0.0',
    fg3m: (sum('fg3m') / n).toFixed(1),
    fg3a: (sum('fg3a') / n).toFixed(1),
    fg3_pct: n > 0 ? ((sum('fg3m') / (sum('fg3a') || 1)) * 100).toFixed(1) : '0.0',
    ftm: (sum('ftm') / n).toFixed(1),
    fta: (sum('fta') / n).toFixed(1),
    ft_pct: n > 0 ? ((sum('ftm') / (sum('fta') || 1)) * 100).toFixed(1) : '0.0',
    turnover: (sum('turnover') / n).toFixed(1),
    min: (sum('min') / n).toFixed(1),
  };
}

/**
 * Compute mean and standard deviation from an array of numbers.
 */
export function computeMeanStdDev(values) {
  if (!values || values.length === 0) return { mean: 0, stdDev: 0 };
  const n = values.length;
  const mean = values.reduce((s, v) => s + v, 0) / n;
  const variance = values.reduce((s, v) => s + (v - mean) ** 2, 0) / n;
  return { mean, stdDev: Math.sqrt(variance) };
}

/**
 * Get the current NBA season year.
 * NBA seasons span two calendar years; the season is named after the first year.
 * e.g. the 2025-26 season → season = 2025.
 */
export function getCurrentSeason() {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth(); // 0-indexed
  // NBA season typically starts in October
  return month >= 9 ? year : year - 1;
}

// ─── League Averages ────────────────────────────────────────────────────────

/**
 * NBA league-wide per-game averages and standard deviations.
 * These represent typical distributions for players who average 10+ minutes.
 * Updated for the 2025-26 season based on historical NBA trends.
 *
 * NOTE: For a production app you'd fetch these dynamically from the API.
 * Using well-researched reference values here so the bell curves render
 * immediately without an extra API call.
 */
export const LEAGUE_AVERAGES = {
  pts: { mean: 14.2, stdDev: 6.8 },
  reb: { mean: 5.2, stdDev: 2.9 },
  ast: { mean: 3.4, stdDev: 2.4 },
  stl: { mean: 0.8, stdDev: 0.4 },
  blk: { mean: 0.5, stdDev: 0.4 },
  fg3m: { mean: 1.6, stdDev: 1.1 },
};
